import type { components } from '@/generated/internal-api/openapi-types';
import type { Animal } from './Animal.model';
import type { Cage } from './Cage.model';
import type { ApiID, Entity, ID, ISODateTime } from './Common.model';
import type { TeamGlossary, TeamGlossaryApiId } from './Glosary.model';
import type { MetadataFieldValueTypes, MetadataFieldWithValue } from './Metadata.model';
import type { Study } from './Study.model';
import type { TreatmentGroup } from './TreatmentGroup.model';
import type { MainUser } from './User.model';

export type SampleMeasurementKey = 'weight' | 'width' | 'length' | 'depth' | 'volume';

export interface SampleMeasurement {
  key?: SampleMeasurementKey;
  unit: string;
  value: string;
}

export interface SampleType {
  id: ID;
  api_id: TeamGlossaryApiId;
  title: string;
  options: {
    /** keys of measurements that can be captured against a sample type */
    details: Array<SampleMeasurementKey>;
  };
  group?: string;
  group_name?: string;
  description?: unknown;
  type?: string;
  type_name?: string;
}

export interface Sample extends Entity {
  sample_id: string;
  api_id: ApiID<'spl'>;
  comments: string;
  date: ISODateTime;
  collection: Cage;
  type: string;
  user: MainUser;
  study_group?: TreatmentGroup;
  study_link?: Study;
  status: SAMPLE_STATUS;
  timepoint: string;
  /** list of measurements collected against a sample */
  details: Array<SampleMeasurement>;
  // Payload object for HoT
  meta: Record<string, MetadataFieldValueTypes>;
  metadata: Array<MetadataFieldWithValue>;
  subject: Animal;
  study_id: ID;
  created_by?: Pick<MainUser, 'name' | 'email'>;
}

export type SampleEdit = components['schemas']['SampleUpdateV1.schema'][];

export interface SampleDetails {
  length?: SampleMeasurement;
  width?: SampleMeasurement;
  weight?: SampleMeasurement;
  volume?: SampleMeasurement;
  depth?: SampleMeasurement;
}

export interface BulkSamplesFormData {
  sample_type: TeamGlossary;
  date: ISODateTime;
  timepoint?: string;
  comments: string;
  details?: SampleDetails;
  meta: Record<string, MetadataFieldValueTypes>;
}

export interface SampleData {
  cageName?: string;
  subject_id: ID;
  subjectName: string;
  sampleType: string;
  date: ISODateTime;
  details: Record<string, string>;
  timepoint?: string;
  comments: string;
  meta: Record<string, string>;
}

export type SAMPLE_STATUS = 'collected' | 'cancelled' | 'planned';

const SAMPLE_STATUS_DISPLAY: Record<SAMPLE_STATUS, string> = {
  collected: 'Collected',
  cancelled: 'Cancelled',
  planned: 'Planned',
};

// Utility function to get display value for sample status
export const getSampleStatusDisplay = (status: SAMPLE_STATUS | string): string => {
  return SAMPLE_STATUS_DISPLAY[status as SAMPLE_STATUS] ?? status ?? '-';
};
